import { AttachmentMetadata } from "@azure/communication-react";
import { Slot, Toolbar, ToolbarButton, ToolbarDivider } from "@fluentui/react-components";
import { Next24Filled, Previous24Filled, RotateLeft24Filled, RotateRight24Filled, ZoomIn24Filled, ZoomOut24Filled } from "@fluentui/react-icons";
import React from 'react';
import { Document, Page } from 'react-pdf';
import { LoadingCircleIconAnimated } from '../..';
import { mergeStyles } from "@fluentui/react";


const isParseUrlSupported = typeof URL !== 'undefined' && typeof URL.parse === 'function';

/**
 * pdf.js needs either a URL (string) or a File object.
 * Internally, URL.parse is used to parse the URL, but it is not supported by iOS 17.
 * If URL.parse is not supported, we fetch the URL and convert it to a File object.
 */
async function getPdfSource(attachment: AttachmentMetadata, controller: AbortController) {
    if (isParseUrlSupported) {
        // If URL.parse is supported, just use the URL string directly
        return attachment.url;
    } else {
        // Otherwise, fetch and convert to File
        try {
            const response = await fetch(attachment.url, { signal: controller.signal });
            if (!response.ok) throw new Error('Network response was not ok');
            const b = await response.blob();
            const f = new File([b], attachment.name, { type: 'application/pdf' });
            return f;
        } catch (error) {
            return null;
        }
    }
}


export const PdfAttachmentRenderer = (props: { attachment: AttachmentMetadata, isMobile: boolean }) => {
    const { attachment, isMobile } = props;

    const [numPages, setNumPages] = React.useState<number>();
    const [pageNumber, setPageNumber] = React.useState<number>(1);

    const hasPrevPage = React.useMemo(() => pageNumber > 1, [pageNumber]);
    const hasNextPage = React.useMemo(() => numPages !== undefined && pageNumber < numPages, [numPages, pageNumber]);

    const scaleStep = 0.2;
    const minScale = 0.5; // Minimum zoom level
    const maxScale = 3; // Maximum zoom level


    const [scaleFactor, setScaleFactor] = React.useState<number>(1);
    const canZoomIn = React.useMemo(() => scaleFactor < maxScale, [scaleFactor, maxScale]);
    const canZoomOut = React.useMemo(() => scaleFactor > minScale, [scaleFactor, minScale]);

    const zoomIn = React.useCallback(() => {
        setScaleFactor((prevScale) => Math.min(prevScale + scaleStep, 3)); // Limit max zoom to 3x
    }, [scaleStep]);

    const zoomOut = React.useCallback(() => {
        setScaleFactor((prevScale) => Math.max(prevScale - scaleStep, 0.5)); // Limit min zoom to 0.5x
    }, [scaleStep]);

    const gotToPrevPage = React.useCallback(() => {
        if (hasPrevPage) {
            setPageNumber(pageNumber - 1);
        }
    }, [hasPrevPage, pageNumber]);

    const gotToNextPage = React.useCallback(() => {
        if (hasNextPage) {
            setPageNumber(pageNumber + 1);
        }
    }, [hasNextPage, pageNumber]);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
    }

    const [rotation, setRotation] = React.useState<number>(0);
    const rotateLeft = React.useCallback(() => {
        setRotation((prevRotation) => (prevRotation + 90) % 360);
    }, [setRotation]);
    const rotateRight = React.useCallback(() => {
        setRotation((prevRotation) => (prevRotation - 90 + 360) % 360);
    }, [setRotation]);


    const [file, setFile] = React.useState<File | string | null>(null);

    React.useEffect(() => {
        const controller = new AbortController();
        getPdfSource(attachment, controller)
            .then((source) => setFile(source))
            .catch((error) => {
                console.error('Error fetching PDF:', error);
            });

        return () => {
            controller.abort();
        };
    }, [attachment]);

    return (
        <div style={{
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden'  //setting height: 100%
        }}>

            <PdfToolbar
                isMobile={isMobile}

                onNextPage={gotToNextPage}
                onPrevPage={gotToPrevPage}
                hasNextPage={hasNextPage}
                hasPrevPage={hasPrevPage}
                pageNumber={pageNumber}
                numPages={numPages ?? 0}

                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                canZoomIn={canZoomIn}
                canZoomOut={canZoomOut}

                onRotateLeft={rotateLeft}
                onRotateRight={rotateRight}
            />

            <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<LoadingCircleIconAnimated size={'small'} />}
                className={mergeStyles({overflow: 'auto'})}
            >
                <Page
                    pageNumber={pageNumber}
                    scale={scaleFactor}
                    rotate={rotation}
                    loading={<LoadingCircleIconAnimated size={'small'} />}
                    className={mergeStyles({zIndex: -1})}
                />
            </Document>
        </div>
    );
};


interface PdfToolbarProps {
    onZoomIn: () => void;
    onZoomOut: () => void;
    onNextPage: () => void;
    onPrevPage: () => void;
    onRotateLeft: () => void;
    onRotateRight: () => void;
    canZoomIn: boolean;
    canZoomOut: boolean;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    pageNumber: number;
    numPages: number;
    isMobile?: boolean;
}

/**
 * PDF Toolbar component for navigating and zooming within a PDF document.
 * Internally users FluentUI's Toolbar component.
 * See https://react.fluentui.dev/?path=/docs/components-toolbar--docs
 *
 * @param props
 * @returns
 */
const PdfToolbar = (props: PdfToolbarProps): React.ReactElement => {
    const {
        onZoomIn,
        onZoomOut,
        onNextPage,
        onPrevPage,
        onRotateLeft,
        onRotateRight,
        canZoomIn,
        canZoomOut,
        hasNextPage,
        hasPrevPage,
        pageNumber,
        numPages,
        isMobile,
    } = props;

    return (
        <Toolbar aria-label="PDF Toolbar">
            {pageNumber} / {numPages}

            <ToolbarDivider />

            <PdfToolbarButton
                icon={<Previous24Filled />}
                disabled={!hasPrevPage}
                onClick={onPrevPage}
                text="Zurück"
                isMobile={isMobile}
            />
            <PdfToolbarButton
                icon={<Next24Filled />}
                disabled={!hasNextPage}
                onClick={onNextPage}
                text="Vor"
                isMobile={isMobile}
            />
            <ToolbarDivider />
            <PdfToolbarButton
                icon={<ZoomOut24Filled />}
                disabled={!canZoomOut}
                onClick={onZoomOut}
                text="Kleiner"
                isMobile={isMobile}
            />
            <PdfToolbarButton
                icon={<ZoomIn24Filled />}
                disabled={!canZoomIn}
                onClick={onZoomIn}
                text="Größer"
                isMobile={isMobile}
            />
            <ToolbarDivider />
            <PdfToolbarButton
                icon={<RotateLeft24Filled />}
                onClick={onRotateLeft}
                text="links"
                isMobile={isMobile}
            />
            <PdfToolbarButton
                icon={<RotateRight24Filled />}
                onClick={onRotateRight}
                text="rechts"
                isMobile={isMobile}
            />


        </Toolbar>
    );
};


interface PdfToolbarButtonProps {
    icon: Slot<'span'>;
    onClick: () => void;
    disabled?: boolean;
    text: string;
    isMobile?: boolean;
}

const PdfToolbarButton = (props: PdfToolbarButtonProps): React.ReactElement => {
    const { icon, onClick, disabled, text, isMobile } = props;

    const vertical = isMobile === false;

    // compact presentation for mobile devices
    return <ToolbarButton vertical={vertical} icon={icon} onClick={onClick} disabled={disabled}>
        {!isMobile && text}
    </ToolbarButton>;
};
